import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "./icons";
import { searchEntries, type SearchAction, type SearchEntry } from "@shared/nav-search";

// 「どこに何があるか」検索。メニュー項目と画面の中の機能を部分一致で探して、その場所を開く。
// キーボード：「/」または Ctrl+K（Mac は ⌘K）で入力欄へ、↑↓で選択、Enter で開く、Esc で閉じる。
const EXAMPLES = ["督促状", "補助金", "給与", "社印", "パスワード", "色"];

export default function NavSearch({ entries, canSearchDeals, canSearchCustomers, onAction, autoFocus, onDone, shortcut }: {
  entries: SearchEntry[];
  canSearchDeals: boolean;
  canSearchCustomers: boolean;
  onAction: (a: SearchAction) => void;
  autoFocus?: boolean;
  onDone?: () => void;   // 開いたあと（スマホの検索窓を閉じる）
  shortcut?: boolean;    // 「/」「Ctrl+K」で入力欄へ（画面に1つだけ）
}) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(!!autoFocus);
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const results = useMemo(() => searchEntries(entries, q), [entries, q]);
  const term = q.trim();
  const extras = term ? [
    ...(canSearchDeals ? [{ key: "deals", label: `案件で「${term}」を探す`, to: `/deals?q=${encodeURIComponent(term)}` }] : []),
    ...(canSearchCustomers ? [{ key: "customers", label: `顧客（店舗）で「${term}」を探す`, to: `/customers?q=${encodeURIComponent(term)}` }] : []),
  ] : [];
  const total = results.length + extras.length;
  useEffect(() => { setSel(0); }, [q]);

  useEffect(() => {
    if (!shortcut) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      if ((e.key === "k" && (e.ctrlKey || e.metaKey)) || (e.key === "/" && !typing)) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [shortcut]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => { if (!boxRef.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("touchstart", onDown); };
  }, [open]);

  const go = (to: string) => {
    if (/^https?:\/\//.test(to)) window.open(to, "_blank", "noopener");
    else nav(to);
  };
  const choose = (i: number) => {
    const r = results[i];
    const x = extras[i - results.length];
    if (r?.action) onAction(r.action);
    else if (r?.to) go(r.to);
    else if (x) go(x.to);
    else return;
    setQ(""); setOpen(false); inputRef.current?.blur(); onDone?.();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setSel((s) => (total ? (s + 1) % total : 0)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => (total ? (s - 1 + total) % total : 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (total) choose(sel); }
    else if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); onDone?.(); }
  };

  const optId = (i: number) => `${listId}-o${i}`;
  return (
    <div ref={boxRef} className="relative w-full">
      <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500">
        <Icon name="search" className="h-4 w-4 shrink-0 text-gray-400" />
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          className="min-w-0 flex-1 bg-transparent py-2 text-sm outline-none"
          placeholder="機能・画面を検索（例：督促状、補助金、社印）"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-label="機能・画面を検索"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && total ? optId(sel) : undefined}
        />
        {q ? (
          <button type="button" className="shrink-0 text-gray-400 hover:text-gray-600" onClick={() => { setQ(""); inputRef.current?.focus(); }} aria-label="検索語を消す"><Icon name="close" className="h-4 w-4" /></button>
        ) : shortcut ? (
          <kbd className="hidden shrink-0 rounded border border-gray-200 px-1.5 text-[10px] text-gray-400 lg:inline">/</kbd>
        ) : null}
      </div>

      {open && (
        <div id={listId} role="listbox" aria-label="検索結果" className="absolute left-0 right-0 z-50 mt-1 max-h-[70vh] overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-xl">
          {!term && (
            <div className="px-3 py-2 text-xs text-gray-500">
              <div className="mb-1.5">どこに何があるかを、言葉の一部で探せます。</div>
              <div className="flex flex-wrap gap-1.5">
                {EXAMPLES.map((ex) => (
                  <button key={ex} type="button" className="rounded-full border border-gray-200 px-2.5 py-1 text-gray-600 hover:border-brand-300 hover:text-brand-700"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => { setQ(ex); inputRef.current?.focus(); }}>{ex}</button>
                ))}
              </div>
            </div>
          )}
          {term && results.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">「{term}」に一致する機能・画面は見つかりませんでした。{extras.length ? "名前で探す場合は下から。" : ""}</div>
          )}
          {results.map((r, i) => (
            <button key={r.id} id={optId(i)} type="button" role="option" aria-selected={sel === i}
              className={`block w-full px-3 py-2 text-left ${sel === i ? "bg-brand-50" : "hover:bg-gray-50"}`}
              onMouseEnter={() => setSel(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(i)}>
              <div className="text-sm font-medium text-gray-900">{r.label}</div>
              <div className="text-[11px] text-gray-500">{r.path.join(" › ")}{r.hint ? `　— ${r.hint}` : ""}</div>
            </button>
          ))}
          {extras.length > 0 && (
            <div className={results.length ? "mt-1 border-t border-gray-100 pt-1" : ""}>
              {extras.map((x, j) => {
                const i = results.length + j;
                return (
                  <button key={x.key} id={optId(i)} type="button" role="option" aria-selected={sel === i}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-brand-700 ${sel === i ? "bg-brand-50" : "hover:bg-gray-50"}`}
                    onMouseEnter={() => setSel(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(i)}>
                    <Icon name="search" className="h-4 w-4 shrink-0" />{x.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
